import type Store from '@ember-data/store';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import type { HomePageAlertType } from 'parog-web/models/alert-triggered-journal-event';
import type CommentWrittenJournalEvent from 'parog-web/models/comment-written-journal-event';
import type NotificationTriggeredJournalEvent from 'parog-web/models/notification-triggered-journal-event';
import type SemeiaSessionService from 'parog-web/services/session';
import NoContentMessage from 'ui-kit/components/no-content-message';
import fuiDropdown from 'wise-fui-modules/modifiers/fui-dropdown';

interface Signature {
  Element: HTMLDivElement;
  Args: {
    item: HomePageAlertType | NotificationTriggeredJournalEvent;
    onClose: () => void;
    commentWrittenJe?: CommentWrittenJournalEvent;
    shouldHideSuggestion?: boolean;
  };
}

export default class AddComment extends Component<Signature> {
  @service declare session: SemeiaSessionService;
  @service declare store: Store;

  @tracked comment = this.args.commentWrittenJe?.comment || '';

  suggestionComments = [
    'Erreur de renseignement de la donnée',
    'Initiation consultation',
    'Hospitalisation',
    "Prescription d'examens complémentaires",
    "Modification d'une prescription médicamenteuse",
    'Contact du patient'
  ];

  get isEditing(): boolean {
    return Boolean(this.args.commentWrittenJe);
  }

  onInputChange = e => {
    this.comment = e.target.value;
  };

  onSave = task(async () => {
    if (this.comment == '') return;
    if (this.args.commentWrittenJe) {
      this.args.commentWrittenJe.comment = this.comment;
      this.args.commentWrittenJe.creator =
        this.session.medic || this.args.commentWrittenJe.creator;
      await this.args.commentWrittenJe.save();
    } else {
      await this.store
        .createRecord('comment-written-journal-event', {
          patient: this.args.item.patient,
          comment: this.comment,
          originJournalEvent: this.args.item,
          creator: this.session.medic,
          occurredAt: new Date()
        })
        .save();
    }
    this.args.onClose(); //Close panel
    this.comment = ''; //Reset the comment
  });

  stopPropagation = (e: Event) => {
    e.preventDefault();
    e.stopPropagation();
  };

  <template>
    {{! eslint-disable ember/template-no-invalid-interactive }}
    {{! TODO: this component was refactored and the violation was fixed }}
    <div
      class='mx-0 my-4 flex flex-col rounded-[0.5em] shadow-[0_0_1em_0_rgba(0,0,0,0.5)]'
      data-test={{@commentWrittenJe?.id}}
      {{on 'click' this.stopPropagation}}
      ...attributes
    >
      <i
        class='icon times mt-2 w-full pr-4 text-end'
        role='button'
        data-test-comment-close-icon
        {{on 'click' @onClose}}
      ></i>
      <div class='flex'>
        <div class='ui form flex w-full flex-col text-center'>
          <div class='mx-4 mb-2 mt-4'>
            <textarea
              value={{this.comment}}
              rows={{3}}
              aria-label='Commentaire'
              class='ui input'
              {{on 'input' this.onInputChange}}
              data-test-edit-comment-section
            ></textarea>
          </div>
          <div class='mx-4 mb-2 mt-4' data-test-save-comment>
            <button
              class='ui primary button !m-0
                {{if this.onSave.isRunning "loading"}}'
              type='button'
              {{on 'click' (perform this.onSave)}}
              disabled={{this.onSave.isRunning}}
            >
              {{if this.isEditing 'Modifier le' 'Ajouter un'}}
              commentaire
            </button>
            <NoContentMessage>
              Le commentaire n'est pas partagé avec le patient.
            </NoContentMessage>
          </div>
        </div>
        {{#unless @shouldHideSuggestion}}
          <div class='field'>
            <div
              {{fuiDropdown}}
              class='ui icon top pointing dropdown basic button [&.ui.button]:my-4 [&.ui.button]:-ml-2 [&.ui.button]:mr-2 [.ui.dropdown_.menu>&>div]:whitespace-normal'
              data-test-suggestion-dropdown
            >
              <i class='lightbulb icon outline'></i>
              <div class='ui dropdown menu left w-[300px]'>
                {{#each this.suggestionComments as |suggestion|}}
                  <div
                    role='button'
                    class='item !whitespace-normal'
                    {{on 'click' (fn (mut this.comment) suggestion)}}
                    data-test-comment-suggestion='{{suggestion}}'
                  >
                    {{suggestion}}
                  </div>
                {{/each}}
              </div>
            </div>
          </div>
        {{/unless}}
      </div>
    </div>
  </template>
}
